import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ChevronDown, Home } from "lucide-react";
import { CustomizeSheet } from "@/components/dashboard/CustomizeSheet";
import { WIDGETS, WidgetCard } from "@/components/dashboard/widgets";
import { CategorizeCard } from "@/components/dashboard/CategorizeCard";
import { HealthHero } from "@/components/dashboard/HealthHero";
import { SavingsHero } from "@/components/dashboard/SavingsHero";
import { RemainHero } from "@/components/dashboard/RemainHero";
import { Suggestions } from "@/components/dashboard/Suggestions";
import { SetupCard } from "@/components/setup/SetupCard";
import { RecurringReminder } from "@/components/dashboard/RecurringReminder";
import { DemoBadge } from "@/components/common/DemoBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { useHousehold } from "@/context/HouseholdContext";
import { useDashboard } from "@/hooks/use-dashboard";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useFormat } from "@/lib/format";

const MOBILE_LIMIT = 4;

const Loading = () => (
  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="dashboard-loading">
    {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-44 rounded-2xl md:col-span-2" />)}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { active } = useHousehold();
  const navigate = useNavigate();
  const { longDate } = useFormat();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading, isError } = useDashboard();

  if (!active) {
    if (user.platform_role) return <Navigate to="/admin" replace />;
    if (user.organizations.length) return <Navigate to="/pro" replace />;
    if (user.partner) return <Navigate to="/partenaires" replace />;
    return <EmptyState icon={Home} title="Aucun foyer" text="Créez votre premier foyer pour commencer." action={<Button onClick={() => navigate("/foyer")} data-testid="create-first-household">Créer un foyer</Button>} />;
  }

  const visible = user.preferences.widgets.filter((w) => w.visible && WIDGETS[w.id]);
  const shown = isMobile && !expanded ? visible.slice(0, MOBILE_LIMIT) : visible;

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="dashboard-page">
      <header className="fade-up flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div>
          <div className="eyebrow first-letter:uppercase">{longDate(new Date())}</div>
          <h1 className="mt-1 text-3xl font-extrabold sm:text-4xl" data-testid="dashboard-greeting">Bonjour, {user.name.split(" ")[0]}</h1>
          <p className="mt-2 hidden text-sm text-muted-foreground sm:block">Voici l'essentiel de <span className="font-medium text-foreground">{active.name}</span>.</p>
        </div>
        <div className="flex items-center gap-2">
          {data?.is_demo && <DemoBadge />}
          <CustomizeSheet />
        </div>
      </header>

      {!user.read_only && <RemainHero />}
      {!user.read_only && <SavingsHero />}
      {!user.read_only && <SetupCard space="personal" />}
      {!user.read_only && <HealthHero />}
      {!user.read_only && <CategorizeCard />}
      {!user.read_only && <RecurringReminder />}
      {!user.read_only && <Suggestions />}

      {isError ? <EmptyState title="Tableau de bord indisponible" text="Impossible de charger les données de ce foyer. Réessayez plus tard." testid="dashboard-error" /> : isLoading || !data ? <Loading /> : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="dashboard-widget-grid">
          {shown.map((w, i) => {
            const def = WIDGETS[w.id];
            return (
              <WidgetCard key={w.id} id={w.id} title={def.title} icon={def.icon} dark={def.dark} tone={def.tone} className={def.span} delay={i * 60}>
                <def.Comp data={data} />
              </WidgetCard>
            );
          })}
          {!visible.length && <EmptyState title="Aucun widget affiché" text="Utilisez « Personnaliser » pour choisir vos priorités." />}
        </div>
      )}

      {isMobile && visible.length > MOBILE_LIMIT && (
        <Button variant="ghost" className="w-full rounded-full" onClick={() => setExpanded(!expanded)} data-testid="dashboard-show-more-button">
          {expanded ? "Afficher moins" : `Afficher ${visible.length - MOBILE_LIMIT} widgets de plus`}
          <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </Button>
      )}
    </div>
  );
}
